import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import './SlideshowPortal.css';

const SlideshowPortal = ({ media, onClose, startIndex = 0 }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [timer, setTimer] = useState(null);
    const [shuffledMedia, setShuffledMedia] = useState([]);
    const [nextImageLoaded, setNextImageLoaded] = useState(false);
    const [controlsVisible, setControlsVisible] = useState(false);
    const controlsTimer = useRef(null);
    const nextImageRef = useRef(null);

    // Shuffle media array on mount
    useEffect(() => {
        const shuffleArray = (array) => {
            const newArray = [...array];
            for (let i = newArray.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
            }
            return newArray;
        };

        setShuffledMedia(shuffleArray(media));
    }, [media]);

    // Handle mouse/touch movement to show/hide controls
    const handleMovement = () => {
        setControlsVisible(true);

        if (controlsTimer.current) {
            clearTimeout(controlsTimer.current);
        }

        controlsTimer.current = setTimeout(() => {
            setControlsVisible(false);
        }, 2000);
    };

    // Set up movement listeners
    useEffect(() => {
        document.addEventListener('mousemove', handleMovement);
        document.addEventListener('touchstart', handleMovement);

        return () => {
            document.removeEventListener('mousemove', handleMovement);
            document.removeEventListener('touchstart', handleMovement);
            if (controlsTimer.current) {
                clearTimeout(controlsTimer.current);
            }
        };
    }, []);

    const currentItem = shuffledMedia[currentIndex];
    const nextItem = shuffledMedia[(currentIndex + 1) % shuffledMedia.length];
    const isCurrentVideo = currentItem?.type === 'video';
    const isNextVideo = nextItem?.type === 'video';

    // Preload next image
    useEffect(() => {
        if (!shuffledMedia.length || isCurrentVideo) return;

        const nextIndex = (currentIndex + 1) % shuffledMedia.length;
        const nextItem = shuffledMedia[nextIndex];

        if (nextItem && !isNextVideo) {
            setNextImageLoaded(false);
            nextImageRef.current = new Image();
            nextImageRef.current.onload = () => {
                setNextImageLoaded(true);
            };
            nextImageRef.current.src = nextItem.originalPath;
        }
    }, [currentIndex, shuffledMedia, isCurrentVideo]);

    // Manage body overflow
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, []);

    // Handle slideshow timer
    useEffect(() => {
        if (!shuffledMedia?.length) return;

        if (timer) clearTimeout(timer);

        if (!isCurrentVideo && nextImageLoaded) {
            const newTimer = setTimeout(() => {
                setCurrentIndex((prevIndex) =>
                    prevIndex === shuffledMedia.length - 1 ? 0 : prevIndex + 1
                );
            }, 3000);
            setTimer(newTimer);
        }

        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [currentIndex, shuffledMedia, isCurrentVideo, nextImageLoaded]);

    const handleVideoEnd = () => {
        setCurrentIndex((prevIndex) =>
            prevIndex === shuffledMedia.length - 1 ? 0 : prevIndex + 1
        );
    };

    if (!shuffledMedia?.length) return null;

    return createPortal(
        <div
            className={`slideshow-overlay ${controlsVisible ? 'controls-visible' : ''}`}
            onClick={onClose}
        >
            <div className="slideshow-content" onClick={e => e.stopPropagation()}>
                <div className={`slideshow-controls ${controlsVisible ? 'visible' : ''}`}>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onClose();
                        }}
                        className="slideshow-button"
                        aria-label="Close"
                    >
                        <X size={24}/>
                    </button>
                </div>
                <div className="slideshow-image-container">
                    <div className="slideshow-image-wrapper">
                        {isCurrentVideo ? (
                            <video
                                key={currentItem.originalPath}
                                src={currentItem.originalPath}
                                className="slideshow-image"
                                autoPlay
                                controls
                                onEnded={handleVideoEnd}
                            />
                        ) : (
                            <img
                                key={currentItem.originalPath}
                                src={currentItem.originalPath}
                                alt=""
                                className="slideshow-image"
                                style={{
                                    opacity: 1,
                                    transition: 'opacity 0.3s ease-in-out'
                                }}
                            />
                        )}

                        {/* Hidden preload image for next slide */}
                        {!isNextVideo && nextItem && (
                            <img
                                src={nextItem.originalPath}
                                alt=""
                                style={{ display: 'none' }}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default SlideshowPortal;